import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Rocket, Check } from "lucide-react";

// Which route each step deep-links to when the badge is clicked.
const STEP_HREF = {
    essence: "/app/tone-of-voice",
    sales_tone: "/app/tone-of-voice",
    recruiter_tone: "/app/tone-of-voice",
    icp: "/app/workspace",
    integrations: "/app/integrations",
    first_action: "/app/sales/signals",
};
const STEP_ORDER = ["essence", "sales_tone", "recruiter_tone", "icp", "integrations", "first_action"];

/**
 * Compact sidebar progress badge — click deep-links to the FIRST incomplete
 * step. Auto-hides at 100% and stays hidden until the user restarts onboarding.
 */
export default function SetupProgressBadge() {
    const { t } = useTranslation();
    const { workspace } = useAuth();
    const location = useLocation();
    const [status, setStatus] = useState(null);

    // Re-fetch when the user navigates so ticks update after tone-tuning/integrations pages.
    useEffect(() => {
        api.get("/onboarding/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
    }, [workspace?.id, location.pathname]);

    if (!status) return null;
    if (status.completion_pct >= 100) return null;

    // Deep-link to first incomplete step
    const firstOpen = STEP_ORDER.find((k) => !status.steps?.[k]);
    const href = STEP_HREF[firstOpen] || "/app/dashboard";
    const pct = status.completion_pct || 0;
    const nextLabel = t(`setup.step_${firstOpen}`);
    const isDone = pct >= 100;

    return (
        <Link
            to={href}
            data-testid="sidebar-setup-badge"
            title={`${t("setup.title")} · ${t("setup.complete_pct", { n: pct })} · ${nextLabel}`}
            className="mt-2 flex items-center gap-2 px-2 py-1.5 rounded-lg border border-[var(--jr-brand)]/25 bg-[var(--jr-brand)]/8 hover:bg-[var(--jr-brand)]/14 transition-colors group"
        >
            <div className="relative w-4 h-4 shrink-0">
                <svg viewBox="0 0 20 20" className="w-4 h-4 -rotate-90">
                    <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5"/>
                    <circle cx="10" cy="10" r="8" fill="none" stroke="var(--jr-brand)" strokeWidth="2.5"
                        strokeDasharray={`${(pct / 100) * 50.27} 50.27`} strokeLinecap="round"/>
                </svg>
                {isDone && <Check size={9} className="absolute inset-0 m-auto text-[var(--jr-brand)]"/>}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)] leading-tight">
                    Setup {pct}%
                </div>
                <div className="text-[10px] text-[var(--jr-text-3)] truncate group-hover:text-[var(--jr-text-2)] leading-tight">
                    → {nextLabel}
                </div>
            </div>
            <Rocket size={11} className="text-[var(--jr-brand)] opacity-60 group-hover:opacity-100 shrink-0"/>
        </Link>
    );
}
